import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import usePublicationsBatch from '@ubeya/shared/hooks/account/usePublicationsBatch';
import {
  Modal as BaseModal,
  FlexMiddle,
  Header2SemiBold,
  Header2,
  ButtonHeader as BaseButtonHeader,
  Header as BaseHeader,
  FlexColumn,
  Table,
  NameRenderer,
  CheckedTimeAgoWithTime
} from '@ubeya/shared-web/components';
import { media, responsiveShow } from '@ubeya/shared-web/style';
import useTableSort from '@ubeya/shared/hooks/useTableSort';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';

const Modal = styled(BaseModal)`
  .title {
    padding: 17px 40px;
  }
`;

const Type = styled(Header2SemiBold)`
  margin-right: 20px;
`;

const Header = styled(BaseHeader).attrs(() => ({ combined: true }))`
  ${responsiveShow.mobile}

  > div {
    padding: 17px 40px;

    > div {
      padding: 0;
    }
  }
`;

const ButtonHeader = styled(BaseButtonHeader)`
  margin-right: 20px;
`;

const Container = styled(FlexColumn)`
  flex: 1;
  ${media.tablet`padding:0 42px 34px 42px;`}
`;

const StatsBatchModal = ({ publicationIds, title: insideTitle, onClose }) => {
  const { t } = useContext(ConfigContext);
  const { isLoading, uniqueUsersMapping } = usePublicationsBatch({ publicationIds });

  const columns = useMemo(
    () => [
      {
        Header: t('name'),
        dataKey: 'name',
        Cell: NameRenderer,
        sortable: true,
        format: 'user'
      },
      {
        Header: t('seen'),
        dataKey: 'seen',
        Cell: CheckedTimeAgoWithTime,
        sortable: true,
        format: 'date'
      }
    ],
    [t]
  );

  const users = useMemo(() => Object.values(uniqueUsersMapping), [uniqueUsersMapping]);
  const seenUsers = useMemo(() => users.filter(({ seen }) => seen), [users]);
  const answeredUsers = useMemo(() => users.filter(({ engaged, booked }) => engaged || booked), [users]);

  const title = useMemo(
    () => (
      <FlexMiddle>
        <Type>{t(insideTitle)}</Type>

        <Header2>
          {t('viewableBy', { total: users.length })} | {t('seenBy', { total: seenUsers.length })}{' '}
          {users.length > 0 ? `(${Math.round((seenUsers.length * 100) / users.length)}%)` : ''}
        </Header2>
      </FlexMiddle>
    ),
    [seenUsers.length, t, insideTitle, users.length]
  );

  const [sort, setSort] = useState({ sortBy: 'seen', sortDirection: 'desc' });
  const sortedData = useTableSort({ data: users, columns, ...sort });
  return (
    <>
      <Modal
        usePortal
        title={title}
        showConfirmButton={false}
        isPaddingContent={false}
        isScrollableContent={false}
        onClose={onClose}>
        <Header
          leftSide={
            <>
              <ButtonHeader>{t('publishNotSeen', { total: users.length - seenUsers.length })}</ButtonHeader>
              {insideTitle === 'question' && (
                <ButtonHeader>{t('publishNotAnswered', { total: users.length - answeredUsers.length })}</ButtonHeader>
              )}
            </>
          }
        />

        <Container>
          <Table columns={columns} data={sortedData} isLoading={isLoading} shadow={false} {...sort} setSort={setSort} />
        </Container>
      </Modal>
    </>
  );
};

export default StatsBatchModal;
